import {Component, DestroyRef, Inject, Input, OnDestroy, OnInit} from '@angular/core';
import {User, UserLevel} from '../../_types/user';
import {AuthService} from '../../_services/auth.service';
import {LevelsComponent} from '../levels/levels.component';
import {BadgesComponent} from '../badges/badges.component';
import {MAT_DIALOG_DATA, MatDialog} from '@angular/material/dialog';
import {RewardsService} from '../../_services/rewards.service';
import {combineLatest, distinctUntilChanged, EMPTY, map, startWith, Subject, switchMap, takeWhile} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ContentService} from '../../_services/content.service';
import {Community, UserAccessLevel} from '../../_types/communities';
import {LocalStorageService} from '../../_services/local-storage.service';
import {endOfDay, endOfMonth, startOfMonth} from '../../_utils/date';
import {ThemeService} from "../../_services/theme.service";
import {CommunitiesService} from "../../_services/communities.service";

@Component({
    selector: 'app-user-info',
    templateUrl: './user-info.component.html',
    styleUrl: './user-info.component.scss',

})
export class UserInfoComponent implements OnInit, OnDestroy {
    @Input({required: true}) user!: Partial<User>;
    @Input() largeAvatar: boolean = false;
    @Input() hideBadges: boolean = false;
    pointsDate: string = 'allTime'
    userPoints: number = 0;
    startDate?: string;
    endDate?: string;
    isDialog: boolean = false;

    comments: number = 0;
    likes: number = 0;
    votes: number = 0;
    accessLevel: string | null = null;
    community: Community | null = null;
    interval: any;
    level: UserLevel | null = null;
    private changeDate$: Subject<{ startDate?: string, endDate?: string }> = new Subject<{
        startDate?: string,
        endDate?: string
    }>();

    constructor(
        private readonly authService: AuthService,
        private readonly rewardsService: RewardsService,
        private readonly contentService: ContentService,
        private readonly communityService: CommunitiesService,
        private readonly localStorageService: LocalStorageService,
        private readonly dialog: MatDialog,
        private readonly destroyRef: DestroyRef,
        protected themeService: ThemeService,
        @Inject(MAT_DIALOG_DATA) public data: { user: Partial<User>; largeAvatar: boolean; hideBadges: boolean }
    ) {

        if (data && data.user) {
            this.isDialog = true;
            this.user = data.user;
            this.largeAvatar = data.largeAvatar != null ? data.largeAvatar : false;
            this.hideBadges = data.hideBadges != null ? data.hideBadges : false;
        }
        this.contentService.getCurrentDashboardCommunity().subscribe(r => {
            this.community = r;
            if (!this.community?.badges_active) {
                this.hideBadges = true;
            }

        });
    }

    // get userFullName(): string {
    //     const firstName = this.user.first_name || '';
    //     const lastName = this.user.last_name || '';
    //     return firstName.length || lastName.length ? `${firstName} ${lastName}`.trim() : this.user.username;
    // }

    get isMe(): boolean {
        return this.user.id === this.authService.getUser()?.id;
    }

    get lastSelectedCommunity(): Community | null {
        return this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
    }


    ngOnInit(): void {
        if (this.community?.id && this.user?.id && !this.user.is_staff && !this.user.is_superuser) {
            this.communityService.getUserMembership(this.community.id, this.user?.id).subscribe(r => {
                if (r.data.level.is_active) {
                    this.level = r.data.level;
                }
                this.accessLevel = r.data.access_level
                if (this.accessLevel == 'member') {
                    this.updateUserPoints();
                    if (this.largeAvatar) {
                        // If own users profile on sidebar
                        // Update the users points every half minute
                        let that = this;
                        this.interval = setInterval(function (): void {
                            that.updateUserPoints();
                        }, 30000);
                    }
                }

            }, e => {
                console.error(e)
            })
        }
    }

    updateUserPoints(): void {
        combineLatest([
            this.contentService.getCurrentDashboardCommunity().pipe(startWith(this.lastSelectedCommunity)),
            this.changeDate$.pipe(startWith({startDate: this.startDate, endDate: this.endDate}))
        ]).pipe(
            map(([community, trigger]) => ({community, trigger})),
            distinctUntilChanged((prev, cur) => {
                return prev.community?.id === cur.community?.id && prev.trigger?.startDate === cur.trigger?.startDate && prev.trigger?.endDate === cur.trigger?.endDate
            }),
            takeWhile(() => !this.user?.points_in_community),
            takeUntilDestroyed(this.destroyRef),
            switchMap(res => {
                this.userPoints = 0;
                if (!res.community) return EMPTY;
                this.getSecondaryStats()
                return this.rewardsService.getUserPoints(res.community.id, this.user.id, this.startDate, this.endDate)
            }),
        ).subscribe(res => {
            this.userPoints = res.points;
        });
    }


    ngOnDestroy(): void {
        this.changeDate$.next({});
        this.changeDate$.complete();
        clearTimeout(this.interval);
    }

    openLevels(): void {
        this.dialog.open(LevelsComponent, {
            panelClass: 'mediumWindow',
            data: {
                user: this.user,
                community: this.community
            }
        });
    }

    openBadges(): void {
        this.dialog.open(BadgesComponent, {panelClass: 'mediumWindow', maxHeight: '80vh'});
    }

    getPercent(value: number, requiredPoints: number): number {
        return (value * 100) / (requiredPoints || 1);
    }

    getUserPoints(event: 'allTime' | 'lastYear' | 'month'): void {
        this.pointsDate = event;
        const currentDate: Date = new Date();
        switch (event) {
            case "lastYear":
                this.endDate = this.formatDateToYYYYMMDD(new Date());
                const previousYearDate: Date = new Date(currentDate);
                previousYearDate.setFullYear(currentDate.getFullYear() - 1);
                this.startDate = this.formatDateToYYYYMMDD(endOfDay(previousYearDate)!);
                break;
            case "month":
                this.startDate = this.formatDateToYYYYMMDD(startOfMonth(new Date())!);
                this.endDate = this.formatDateToYYYYMMDD(endOfMonth(new Date())!);
                break;
            case "allTime":
                this.endDate = undefined;
                this.startDate = undefined;
                break;
        }
        this.changeDate$.next({startDate: this.startDate, endDate: this.endDate});
    }

    getSecondaryStats(): void {
        if (this.user.id) {
            this.contentService.userStats(this.lastSelectedCommunity?.id || 0, this.user.id, this.endDate, this.startDate).subscribe(r => {
                this.comments = r.comments;
                this.likes = r.likes;
                this.votes = r.votes;
            }, e => {
                console.error(e)
            })
        }
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.contentService.userHasCommunityRole(roles);
    }

    private formatDateToYYYYMMDD(date: Date) {
        if (!date) return undefined;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() returns 0-11, so add 1
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }
}
